import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Archive, RotateCcw } from 'lucide-react';
import { api } from '../../context/AppContext';
import { toast } from 'sonner';

/**
 * Saved Plans Archive — "Le Coffre-Fort"
 * Displays archived training plans with restore functionality.
 */
export default function SavedPlansArchive({ userId, isCoachOrAdmin, language, onPlanRestored }) {
  const fr = language === 'fr';
  const [show, setShow] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleArchive = async () => {
    const newShow = !show;
    setShow(newShow);
    if (newShow && plans.length === 0) {
      setLoading(true);
      try {
        const res = await api.get(`/coach/command/armurerie/${userId}/plans`);
        setPlans((res.data.plans || []).filter(p => p.status === 'archived'));
      } catch { /* */ }
      finally { setLoading(false); }
    }
  };

  const restorePlan = async (planId) => {
    try {
      await api.post(`/coach/command/armurerie/${userId}/plans/${planId}/restore`);
      toast.success(fr ? 'Plan restaure!' : 'Plan restored!');
      onPlanRestored?.();
      setShow(false);
    } catch {
      toast.error(fr ? 'Erreur' : 'Error');
    }
  };

  return (
    <>
      <div className="flex justify-end">
        <button
          onClick={toggleArchive}
          className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary transition-colors"
          data-testid="saved-plans-toggle"
        >
          <Archive className="w-3.5 h-3.5" />
          {fr ? 'Mes Anciens Plans' : 'My Saved Plans'}
        </button>
      </div>

      {show && (
        <Card className="border-border bg-card" data-testid="saved-plans-list">
          <CardContent className="p-3 space-y-2">
            <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">
              {fr ? 'Coffre-Fort — Plans Archives' : 'Vault — Archived Plans'}
            </p>
            {loading ? (
              <p className="text-xs text-muted-foreground/60 italic text-center py-3">...</p>
            ) : plans.length === 0 ? (
              <p className="text-xs text-muted-foreground/60 italic text-center py-3">
                {fr ? 'Aucun ancien plan' : 'No saved plans'}
              </p>
            ) : plans.map((sp, i) => (
              <div key={sp.plan_id || i} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/50 border border-border">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">{sp.plan_name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {sp.deployed_at ? new Date(sp.deployed_at).toLocaleDateString('fr-CA') : ''} — {sp.weeks?.length || '?'} {fr ? 'sem.' : 'wks'}
                  </p>
                </div>
                <button
                  onClick={() => restorePlan(sp.plan_id)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-[10px] font-bold hover:bg-primary/20 transition-all shrink-0"
                  data-testid={`restore-saved-plan-${i}`}
                >
                  <RotateCcw className="w-3 h-3" />
                  {fr ? 'Reactiver' : 'Restore'}
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}
